import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTableColumnSettings,
  DataTableFilterButton,
  Icon,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import { PageHeader } from "#app/components/page-header";

import {
  TeamsTable,
  TEAMS_PINNED_COLUMN_IDS,
  buildTeamsFilterDefs,
  useTeamsColumns,
} from "#app/components/teams/teams-table";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { TEAM_LIST } from "#app/lib/filters/list-configs";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { useAppData } from "#app/lib/data";
import { useUserPool } from "#app/components/entity-selectors/hooks/use-user-pool";
import { renderTableEntityFilter } from "#app/components/entity-selectors/table-entity-filter";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { ExpandableSearch } from "@salestrak/ui";
import { useAnalytics, AnalyticsContext, TeamEvent, useTrackSearch } from "#app/lib/analytics";

function TeamsScreen() {
  const { push } = useAppNavigate();
  const { track } = useAnalytics();
  const { currentUser } = useAppData();
  const { removeTeamMembers } = useTeamMutations();
  const isMobile = useIsMobile();
  const canManageTeams = usePermission("manage.teams");

  const { pool: userPool } = useUserPool();
  const filterDefs = useMemo(() => buildTeamsFilterDefs(userPool.items), [userPool.items]);
  const teamFilters = useTableFilters(filterDefs);

  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(TEAM_LIST, { filterWhere: teamFilters.where });
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Team,
    event: TeamEvent.Search,
  }));

  // Dialog state
  const [leaveTeamId, setLeaveTeamId] = useState<string | null>(null);

  // Clicking a team navigates to its dashboard — same destination as
  // the left-hand "Teams" menu (`/team/[teamId]`) — rather than opening the
  // team preview dialog.
  const handleViewTeam = useCallback(
    (teamId: string) => {
      track({ context: AnalyticsContext.Team, event: TeamEvent.Open, data: { teamId } });
      push(`/team/${teamId}`);
    },
    [push, track],
  );

  // Row-menu "Team settings" — the manage page under workspace settings,
  // not the team dashboard. Gated the same as the header's "Manage teams"
  // button; without the permission the menu item is hidden entirely.
  const handleTeamSettings = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}`);
    },
    [push],
  );

  const handleLeaveTeam = useCallback((teamId: string) => {
    setLeaveTeamId(teamId);
  }, []);

  const columns = useTeamsColumns({
    variant: "app",
    onViewTeam: handleViewTeam,
  });
  const tableView = useTableViewSettings("teams:app");

  const handleConfirmLeave = useCallback(async () => {
    if (!leaveTeamId || !currentUser?.userId) return;
    // Distinct from the hook's generic Settings/remove_members: this captures
    // leaving your own team from the teams list as a discrete user action.
    const result = await removeTeamMembers(leaveTeamId, [currentUser.userId]);
    if (result) {
      track({ context: AnalyticsContext.Team, event: TeamEvent.Leave, data: { teamId: leaveTeamId } });
    }
    setLeaveTeamId(null);
  }, [leaveTeamId, currentUser?.userId, removeTeamMembers, track]);

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Teams"
        actions={
          <>
            <DataTableFilterButton
              filters={teamFilters.filters}
              activeFilterCount={teamFilters.activeFilterCount}
              clearAll={teamFilters.clearAll}
              setFilterValues={teamFilters.setFilterValues}
              renderEntityFilter={renderTableEntityFilter}
            />
            <ExpandableSearch
              onValueChange={setSearchValue}
              placeholder="Search teams..."
              size="xs"
            />
            <DataTableColumnSettings
              columns={columns}
              pinnedColumnIds={TEAMS_PINNED_COLUMN_IDS}
              {...tableView.settingsProps}
            />
            {canManageTeams && (
              <Button
                size={isMobile ? "icon-xs" : "xs"}
                onClick={() => push("/settings/teams")}
              >
                {isMobile ? <Icon name="settings" /> : <Text>Manage teams</Text>}
              </Button>
            )}
          </>
        }
      />
      <TeamsTable
        variant="app"
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        columns={columns}
        tableViewProps={tableView.tableProps}
        onViewTeam={handleViewTeam}
        onNavigateToTeam={canManageTeams ? handleTeamSettings : undefined}
        onLeaveTeam={handleLeaveTeam}
      />
      <AlertDialog
        open={!!leaveTeamId}
        onOpenChange={(open) => { if (!open) setLeaveTeamId(null); }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave team?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be removed from this team. You can be re-added later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button variant="destructive" onClick={handleConfirmLeave}>
              Leave team
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/teams")({
  component: TeamsScreen,
});
